import { Button, Empty, Tag, Tooltip, Typography } from 'antd';
import { AlertCircle, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SortableList } from '@shared/components/SortableList';
import type { FormFieldModel } from '../types';
import { FIELD_ICONS } from './fieldMeta';

const { Text } = Typography;

interface FieldListProps {
  fields: FormFieldModel[];
  selectedId: string | null;
  invalidIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onReorder: (next: FormFieldModel[]) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

/** Orta sütun — alanların sıralanabilir (dnd) listesi; satır seç → sağ panelde düzenle. */
export function FieldList({
  fields,
  selectedId,
  invalidIds,
  onSelect,
  onReorder,
  onRemove,
  disabled,
}: FieldListProps) {
  const { t } = useTranslation('forms');
  const td = t as unknown as (key: string) => string;

  if (fields.length === 0) {
    return <Empty description={t('builder.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <SortableList<FormFieldModel>
      items={fields}
      getId={(f) => f.id}
      onReorder={onReorder}
      disabled={disabled}
      renderItem={(f) => {
        const isSelected = f.id === selectedId;
        const isInvalid = invalidIds.has(f.id);
        return (
          <div
            role="button"
            tabIndex={0}
            onClick={() => onSelect(f.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(f.id);
              }
            }}
            data-testid="field-row"
            data-selected={isSelected ? 'true' : 'false'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 8,
              cursor: 'pointer',
              border: `1px solid ${isSelected ? 'var(--ds-brand)' : 'transparent'}`,
              background: isSelected ? 'var(--ds-surface-2)' : 'transparent',
            }}
          >
            <span style={{ color: 'var(--ds-text-tertiary)', display: 'flex' }}>{FIELD_ICONS[f.type]}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Text ellipsis style={{ fontWeight: 500 }}>
                {f.label.trim() || f.key.trim() || td(`builder.fieldType.${f.type}`)}
                {f.required ? <Text type="danger"> *</Text> : null}
              </Text>
              <div style={{ fontSize: 11, color: 'var(--ds-text-tertiary)' }}>
                {td(`builder.fieldType.${f.type}`)}
                {f.key.trim() ? ` · ${f.key.trim()}` : ''}
              </div>
            </div>
            {isInvalid ? (
              <Tooltip title={t('builder.errors.title')}>
                <AlertCircle size={15} color="var(--ds-danger, #dc2626)" />
              </Tooltip>
            ) : null}
            <Tag style={{ marginInlineEnd: 0 }}>{f.type}</Tag>
            {!disabled ? (
              <Button
                type="text"
                size="small"
                danger
                icon={<Trash2 size={14} />}
                aria-label={t('builder.field.remove')}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(f.id);
                }}
              />
            ) : null}
          </div>
        );
      }}
    />
  );
}
